import { ActiveSelection, CanvasElement, CanvasOrSelection } from '@/typings/customilyElements/canvasElements';
import { ListElements, GroupedItems } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';

export const getEpsId = (treeItem: ListElements): number | null => {
    if (treeItem.type === ListElementType.PageEps) {
        return Number(treeItem.element.id);
    } else {
        return treeItem.element.pageEpsId;
    }
};

export const updateItemElement = (items: ListElements[], parent?: ListElements) => {
    for (const item of items) {
        item.parent = parent;
        if (parent) {
            const isParentEps = parent.type === ListElementType.PageEps;
            switch (item.type) {
                case ListElementType.Group: {
                    item.element.pageEpsId = isParentEps ? parent.element.id : parent.element.pageEpsId;
                    item.element.parentId = isParentEps ? null : parent.element.id;
                    break;
                }
                case ListElementType.CanvasElement:
                    item.element.set({
                        pageEpsId: isParentEps ? parent.element.id : parent.element.pageEpsId,
                        groupId: isParentEps ? null : parent.element.id
                    });
                    break;
                case ListElementType.PageEps:
                    // No need to update
                    break;
            }
        } else {
            switch (item.type) {
                case ListElementType.Group:
                    item.element.pageEpsId = null;
                    item.element.parentId = null;
                    break;
                case ListElementType.CanvasElement:
                    item.element.set({
                        pageEpsId: null,
                        groupId: null
                    });
                    break;
                case ListElementType.PageEps:
                    // No need to update
                    break;
            }
        }

        if (item.children && item.children.length) {
            updateItemElement(item.children, item);
        }
    }
};

export const findTreeItemByIds = (items: ListElements[], ids: string[]): ListElements[] => {
    const foundElements: ListElements[] = [];

    const findElements = (items: ListElements[]) => {
        for (const item of items) {
            if (
                (item.element?.uuid && ids.includes(item.element.uuid)) ||
                (!item.element?.uuid && item.element?.id && ids.includes(item.element.id))
            ) {
                foundElements.push(item);
            }

            if (item.children && item.children.length > 0) {
                findElements(item.children);
            }
        }
    };

    findElements(items);
    return foundElements;
};

export const findTreeItemById = (items: ListElements[], id: string): ListElements | null => {
    const findElement = (items: ListElements[]): ListElements | null => {
        for (const item of items) {
            const matches =
                (item.element?.uuid && id.includes(item.element.uuid)) ||
                (!item.element?.uuid && item.element?.id && id.includes(item.element.id));

            if (matches) {
                return item;
            }

            if (item.children && item.children.length > 0) {
                const foundInChildren = findElement(item.children);
                if (foundInChildren) {
                    return foundInChildren;
                }
            }
        }

        return null;
    };

    return findElement(items);
};

export const getUniqueEpsIds = (elements: ListElements[]): number[] => {
    const epsIds = elements.map(getEpsId).filter((id): id is number => id !== null);
    return [...new Set(epsIds)];
};

export const getSiblings = (item: ListElements, treeItems: ListElements[]): ListElements[] =>
    item.parent?.children ?? treeItems;

export const isValidCanvasItem = (item: ListElements | null): item is ListElements =>
    !!item && item.type === ListElementType.CanvasElement;

export const canMoveForward = (index: number): boolean => index > 0;

export const canMoveBackward = (index: number, siblingsLength: number): boolean =>
    index >= 0 && index < siblingsLength - 1;

export const getGroupedSelectedItems = (elements: CanvasElement[], treeItems: ListElements[]): GroupedItems[] => {
    const map = new Map<ListElements[], ListElements[]>();

    for (const element of elements) {
        const item = findTreeItemById(treeItems, element.uuid || element.id.toString());
        if (!isValidCanvasItem(item)) {
            continue;
        }

        const siblings = getSiblings(item, treeItems);

        if (!map.has(siblings)) {
            map.set(siblings, []);
        }

        map.get(siblings)!.push(item);
    }

    return Array.from(map.entries()).map(([siblings, items]) => ({
        siblings,
        items
    }));
};

export const moveGroupedForward = (group: GroupedItems) => {
    const { siblings, items } = group;

    const sorted = [...items].sort((a, b) => siblings.indexOf(a) - siblings.indexOf(b));

    const firstIndex = siblings.indexOf(sorted[0]);
    if (firstIndex <= 0) {
        return;
    }

    for (const item of sorted) {
        const index = siblings.indexOf(item);
        siblings.splice(index, 1);
        siblings.splice(index - 1, 0, item);
    }
};

export const moveGroupedBackward = (group: GroupedItems) => {
    const { siblings, items } = group;

    const sorted = [...items].sort((a, b) => siblings.indexOf(b) - siblings.indexOf(a));

    const lastIndex = siblings.indexOf(sorted[0]);
    if (lastIndex >= siblings.length - 1) {
        return;
    }

    for (const item of sorted) {
        const index = siblings.indexOf(item);
        siblings.splice(index, 1);
        siblings.splice(index + 1, 0, item);
    }
};

export const bringGroupedToFront = (group: GroupedItems) => {
    const { siblings, items } = group;

    const sorted = [...items].sort((a, b) => siblings.indexOf(a) - siblings.indexOf(b));

    const firstIndex = siblings.indexOf(sorted[0]);
    if (firstIndex <= 0) {
        return;
    }

    for (const item of sorted) {
        const index = siblings.indexOf(item);
        siblings.splice(index, 1);
    }

    siblings.unshift(...sorted);
};

export const sendGroupedToBack = (group: GroupedItems) => {
    const { siblings, items } = group;

    const sorted = [...items].sort((a, b) => siblings.indexOf(a) - siblings.indexOf(b));

    const lastIndex = siblings.indexOf(sorted[sorted.length - 1]);
    if (lastIndex >= siblings.length - 1) {
        return;
    }

    for (const item of sorted) {
        const index = siblings.indexOf(item);
        siblings.splice(index, 1);
    }

    siblings.push(...sorted);
};

export const isActiveSelection = (element: CanvasOrSelection): element is ActiveSelection<CanvasElement> =>
    (element as ActiveSelection<CanvasElement>).type === 'activeSelection';
